import type { NavTab } from '../platform/types'
import { useT } from '../i18n'

interface Props {
  active: NavTab
  onNavigate: (tab: NavTab) => void
}

// Active tabs use the JDG logo lime so the bottom bar reads as the
// brand. The label uses the deeper #B8C01F variant for 24px legibility
// on white; the SVG fills can take the brighter brand value.
const ACTIVE = '#D9E026'
const INACTIVE = '#6B7280'

// All three icons constrained to **exactly** y=5..y=19 visual extent
// (14px of vertical content, centred in the 24px viewBox). Stroke
// width 1.5 means visual ink lands on y=4.25..y=19.75 — close enough
// that the eye reads them as identical heights regardless of which
// tab is active. The previous icons varied by 4+ px, which is what
// made the Weekly tab appear to "jump" when selected.
const tabs: { id: NavTab; label: string; icon: (active: boolean) => React.JSX.Element }[] = [
  {
    id: 'hub',
    label: 'Games',
    icon: (active) => (
      <svg viewBox="0 0 24 24" fill="none" className="nav-tab-icon">
        <rect x="5" y="5" width="6" height="6" rx="1.5"
          fill={active ? ACTIVE : 'none'}
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
        <rect x="13" y="5" width="6" height="6" rx="1.5"
          fill={active ? ACTIVE : 'none'}
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
        <rect x="5" y="13" width="6" height="6" rx="1.5"
          fill="none" stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
        <rect x="13" y="13" width="6" height="6" rx="1.5"
          fill="none" stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: 'weekly',
    label: 'Weekly',
    icon: (active) => (
      <svg viewBox="0 0 24 24" fill="none" className="nav-tab-icon">
        {/* Top "tab" marks anchored at y=5 to match the body top */}
        <path d="M8 5v2M16 5v2"
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" strokeLinecap="round" />
        {/* Calendar body — y=7..y=19 */}
        <rect x="4" y="7" width="16" height="12" rx="2"
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
        <path d="M4 11h16"
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="12" cy="14.5" r="2"
          fill={active ? ACTIVE : 'none'}
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
      </svg>
    ),
  },
  {
    id: 'rules',
    label: 'Rules',
    icon: (active) => (
      <svg viewBox="0 0 24 24" fill="none" className="nav-tab-icon">
        {/* Open book — spine at x=12, covers y=5..y=19 to match siblings */}
        <path d="M12 6.5C10.5 5.5 8.5 5 6 5c-.55 0-1 .45-1 1v11c0 .55.45 1 1 1 2.5 0 4.5.5 6 1.5 1.5-1 3.5-1.5 6-1.5.55 0 1-.45 1-1V6c0-.55-.45-1-1-1-2.5 0-4.5.5-6 1.5Z"
          fill={active ? ACTIVE : 'none'}
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M12 6.5v13"
          stroke={active ? '#FFFFFF' : INACTIVE} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    id: 'profile',
    label: 'Profile',
    icon: (active) => (
      <svg viewBox="0 0 24 24" fill="none" className="nav-tab-icon">
        {/* Head: cy=8.5 r=3.5 → y=5..y=12 */}
        <circle cx="12" cy="8.5" r="3.5"
          fill={active ? ACTIVE : 'none'}
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" />
        {/* Shoulders: arc from y=13 down to y=19 */}
        <path d="M5 19c0-3.5 3.13-6 7-6s7 2.5 7 6"
          stroke={active ? ACTIVE : INACTIVE} strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
]

export function BottomNav({ active, onNavigate }: Props) {
  const t = useT()
  return (
    // Edge-to-edge liquid-glass bar — modeled on the iOS 26 tab bar.
    // The recipe:
    //   bg-white/55          — light tint that lets content show through
    //   backdrop-blur-2xl    — heavy gaussian blur (~40px) for the glass
    //   backdrop-saturate-150 — boosts color saturation behind the bar
    //                           so colorful content reads as warm glass
    //   inset highlight       — top 1px of white at 50% opacity sells the
    //                           "thin sheet of glass" specular line
    //   border-t border-white/30 — a softer hairline than the previous /40
    // Outer <nav> spans the full viewport so the bar doesn't visibly end
    // at the column edge on tablet/desktop. The inner wrapper caps the
    // row of buttons to the same column the .screen content uses so the
    // icons don't drift apart on wider screens.
    <nav className="fixed bottom-0 inset-x-0 z-40
                    bg-white/35
                    backdrop-blur-2xl backdrop-saturate-180
                    shadow-[inset_0_1px_0_rgba(255,255,255,0.7),0_-2px_24px_rgba(0,0,0,0.08)]
                    pb-(--sat-bottom,env(safe-area-inset-bottom))">
      <div className="mx-auto w-full max-w-xl md:max-w-2xl lg:max-w-3xl
                      flex items-stretch">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => onNavigate(tab.id)}
            className={`nav-tab ${active === tab.id ? 'nav-tab-active' : ''}`}
          >
            {tab.icon(active === tab.id)}
            <span className="text-[10px] md:text-xs font-medium">{t(tab.label)}</span>
          </button>
        ))}
      </div>
    </nav>
  )
}
